import UniDataTable from '@/common/classes/UniDataTable'
import UniDataTableRow from '@/common/classes/UniDataTableRow'

function isTableLikeInner(v: unknown): v is { rows?: unknown[]; columns?: unknown; empty?: boolean; attributes?: unknown } {
  return v != null && typeof v === 'object' && !Array.isArray(v) && Array.isArray((v as { rows?: unknown }).rows)
}

function cloneTableInner(inner: {
  rows?: unknown[]
  columns?: unknown
  empty?: boolean
  attributes?: unknown
}): { rows: unknown[]; columns?: string[]; empty: boolean; attributes?: Record<string, unknown> } {
  const rows = Array.isArray(inner.rows) ? inner.rows : []
  const clonedRows = rows.map((row) => {
    if (row instanceof UniDataTableRow) {
      return row.clone()
    }
    return new UniDataTableRow(row as any, 0, () => {})
  })
  const out: { rows: unknown[]; columns?: string[]; empty: boolean; attributes?: Record<string, unknown> } = {
    rows: clonedRows,
    empty: clonedRows.length === 0 ? true : inner.empty === true ? true : false,
  }
  if (Array.isArray(inner.columns)) {
    out.columns = [...inner.columns] as string[]
  }
  if (inner.attributes != null && typeof inner.attributes === 'object') {
    out.attributes = { ...(inner.attributes as Record<string, unknown>) }
  }
  return out
}

/** Deep-clone table payload before storing under another field name (Swing setField parity). */
export function cloneTableValueForSetField(value: unknown): unknown {
  if (value == null) return value

  if (value instanceof UniDataTable) {
    return new UniDataTable(cloneTableInner(value))
  }

  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'type' in (value as object) &&
    'value' in (value as object)
  ) {
    const typed = value as { type?: string; value?: unknown }
    const t = String(typed.type ?? '')
    if (t === 'UniDataTable' || t === 'UniDataTableFields') {
      const inner = typed.value
      if (inner instanceof UniDataTable) {
        return { ...typed, value: new UniDataTable(cloneTableInner(inner)) }
      }
      if (isTableLikeInner(inner)) {
        return { ...typed, value: cloneTableInner(inner) }
      }
    }
    return value
  }

  if (isTableLikeInner(value)) {
    return cloneTableInner(value)
  }

  return value
}

export function isTableLikeValueForSetField(value: unknown): boolean {
  if (value instanceof UniDataTable) return true
  if (
    typeof value === 'object' &&
    value != null &&
    !Array.isArray(value) &&
    'type' in value &&
    'value' in value
  ) {
    const t = String((value as { type?: string }).type ?? '')
    if (t === 'UniDataTable' || t === 'UniDataTableFields') return true
  }
  return isTableLikeInner(value)
}

function typedFieldInnerRef(entry: unknown): unknown {
  if (entry == null || typeof entry !== 'object') return entry
  if ('value' in (entry as object)) return (entry as { value?: unknown }).value
  return entry
}

function isTableLikeFieldEntry(entry: unknown): boolean {
  const ref = typedFieldInnerRef(entry)
  return ref != null && typeof ref === 'object' && Array.isArray((ref as { rows?: unknown }).rows)
}

/** Split any table fields that accidentally share the same inner `.value` object. */
export function isolateAllSharedTableFieldValues(fields: Record<string, unknown>): Record<string, unknown> {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return {}
  const out = { ...fields }
  const refToKeys = new Map<unknown, string[]>()

  for (const [key, entry] of Object.entries(out)) {
    if (!isTableLikeFieldEntry(entry)) continue
    const ref = typedFieldInnerRef(entry)
    const keys = refToKeys.get(ref) ?? []
    keys.push(key)
    refToKeys.set(ref, keys)
  }

  for (const keys of refToKeys.values()) {
    if (keys.length <= 1) continue
    for (let i = 1; i < keys.length; i += 1) {
      out[keys[i]!] = cloneTableValueForSetField(out[keys[i]!])
    }
  }

  return out
}

/** Split selection/catalog fields that accidentally share the same table object (form 722). */
export function isolateSharedTableFieldAliases(
  fields: Record<string, unknown>,
  aliasPairs: Array<[selectionField: string, catalogField: string]> = [['addedCategories', 'categories']],
): Record<string, unknown> {
  const out = isolateAllSharedTableFieldValues(fields)
  for (const [selectionField, catalogField] of aliasPairs) {
    const selectionEntry = out[selectionField]
    const catalogEntry = out[catalogField]
    if (selectionEntry == null || catalogEntry == null) continue
    const selectionRef = typedFieldInnerRef(selectionEntry)
    const catalogRef = typedFieldInnerRef(catalogEntry)
    if (selectionRef != null && selectionRef === catalogRef) {
      out[selectionField] = cloneTableValueForSetField(selectionEntry)
    }
  }
  return out
}

export function normalizeDialogPageUniDataFieldAliases(pageData: Record<string, unknown> | null | undefined): void {
  if (!pageData || typeof pageData !== 'object' || Array.isArray(pageData)) return
  const uniData = (pageData as { uniData?: { fields?: { fields?: Record<string, unknown> } } }).uniData
  const fields = uniData?.fields?.fields
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return
  uniData!.fields!.fields = isolateSharedTableFieldAliases(isolateAllSharedTableFieldValues(fields))
}
